// ─────────────────────────────────────────────
//  useAppVersion — is the bundle this tab is running still the deployed one?
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  ⚠️ THE PROBLEM IS A TAB THAT IS NEVER RELOADED, NOT A CACHE. `firebase.json`
//  already serves the SPA shell with `no-cache, no-store, must-revalidate`, so
//  the NEXT load always gets the new build. But this app is left open: a TV
//  board runs a whole shift, a phone sits in a pocket all day. Nothing in a
//  loaded page ever re-reads index.html, so a deploy at 10:00 reaches a wall
//  device at whatever time somebody happens to reload it — which may be never.
//  A fix that is "deployed" is not a fix that is RUNNING.
//
//  (The handoff blamed a CRA service worker for this. There isn't one: no
//  registration in src, no workbox in package.json, and /sw.js on the live site
//  returns the SPA shell as text/html because of the catch-all rewrite. The
//  hosting-cache half was real and is already fixed.)
//
//  HOW THE VERSION IS KNOWN. `asset-manifest.json` is emitted by the build with
//  the content hash of the main bundle, is served by the same no-cache rule, and
//  is already deployed. The tab's OWN version is read from the `<script>` tag
//  that loaded it, so the comparison is "what am I actually running" against
//  "what is deployed" — not two fetches that could both be new while the running
//  code is old.
//
//  ⚠️ IT NEVER RELOADS BY ITSELF, AND THAT IS NOT TIMIDITY. This is a live OR
//  board. An automatic reload would discard a half-typed assignment, close an
//  open modal mid-decision, and do it at whatever moment the poll happened to
//  fire. The hook reports; a human chooses.
// ─────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";

/** Five minutes. Long enough to be invisible, short enough that a shift-long
 *  tab learns about a morning deploy before the afternoon. */
export const VERSION_POLL_MS = 5 * 60 * 1000;

export const ASSET_MANIFEST_URL = "/asset-manifest.json";

/**
 * The main-bundle path this tab is running, read off its own script tag.
 *
 * Returns null when there is no hashed bundle to find — the dev server, a test
 * renderer, an unusual host page. Null means "cannot tell", and every caller
 * treats that as "do not claim there is an update".
 */
export function runningBundlePath(doc = typeof document !== "undefined" ? document : null) {
  if (!doc || typeof doc.querySelectorAll !== "function") return null;
  const scripts = Array.from(doc.querySelectorAll("script[src]"));
  for (const el of scripts) {
    const src = el.getAttribute("src") || "";
    // CRA emits /static/js/main.<hash>.js. The hash is the whole signal.
    if (/\/static\/js\/main\.[^/]+\.js$/.test(src)) return src;
  }
  return null;
}

/** The deployed main-bundle path, out of the build's asset manifest. */
export function deployedBundlePath(manifest) {
  const files = manifest && typeof manifest === "object" ? manifest.files : null;
  const main = files && typeof files === "object" ? files["main.js"] : null;
  return typeof main === "string" && main ? main : null;
}

/**
 * Compare, conservatively.
 *
 * ⚠️ ANY UNCERTAINTY MEANS "NO UPDATE". A banner that appears when nothing has
 * changed teaches people to dismiss it, and then it is not there when it
 * matters. Only a confident difference between two known strings counts.
 */
export function isOutdated(running, deployed) {
  if (!running || !deployed) return false;
  return running !== deployed;
}

/**
 * Poll the deployed manifest and report whether this tab is behind.
 *
 * @param {object} [opts]
 *   @param {number}   [opts.intervalMs]  poll period
 *   @param {Function} [opts.fetchImpl]   injected for tests
 *   @param {Function} [opts.getRunning]  injected for tests
 *   @param {boolean}  [opts.enabled]     false disables polling entirely
 * @returns {{ outdated: boolean, deployed: string|null, running: string|null,
 *             dismiss: Function, check: Function }}
 */
export function useAppVersion(opts = {}) {
  const {
    intervalMs = VERSION_POLL_MS,
    fetchImpl = typeof fetch !== "undefined" ? fetch : null,
    getRunning = runningBundlePath,
    enabled = true,
  } = opts;

  const [deployed, setDeployed] = useState(null);
  // The version the user has already said "not now" to. Dismissing must not be
  // undone by the next poll five minutes later — that is nagging, and nagging
  // is how a banner gets ignored on the day it matters.
  const [dismissed, setDismissed] = useState(null);
  const runningRef = useRef(null);
  if (runningRef.current === null) runningRef.current = getRunning();

  const check = useCallback(async () => {
    if (!fetchImpl) return;
    try {
      // Cache-bust explicitly. The hosting header already says no-cache, but a
      // corporate proxy between a hospital tablet and Firebase does not have to
      // honour it, and a cached manifest would mean the banner never appears.
      const res = await fetchImpl(`${ASSET_MANIFEST_URL}?t=${Date.now()}`, { cache: "no-store" });
      if (!res || !res.ok) return;
      const path = deployedBundlePath(await res.json());
      if (path) setDeployed(path);
    } catch {
      // Offline, blocked, malformed — all the same answer: say nothing. A
      // version check that raises a banner on a flaky network is worse than one
      // that quietly waits for the next poll.
    }
  }, [fetchImpl]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const tick = () => {
      // ⚠️ ONLY WHILE THE TAB IS VISIBLE. A background tab polling every five
      // minutes for a whole shift is battery and request volume spent to answer
      // a question nobody is looking at; the visibilitychange handler catches
      // up the moment it is looked at again.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      if (!cancelled) check();
    };
    tick();
    const id = setInterval(tick, intervalMs);
    const onVisible = () => tick();
    if (typeof document !== "undefined" && document.addEventListener) {
      document.addEventListener("visibilitychange", onVisible);
    }
    return () => {
      cancelled = true;
      clearInterval(id);
      if (typeof document !== "undefined" && document.removeEventListener) {
        document.removeEventListener("visibilitychange", onVisible);
      }
    };
  }, [check, intervalMs, enabled]);

  const running = runningRef.current;
  const outdated = isOutdated(running, deployed) && deployed !== dismissed;

  // Plain and dependent on `deployed`. An earlier version folded this into a
  // setDeployed updater to avoid the dependency — but a state updater must be
  // pure, and React runs it twice under StrictMode, so the side effect fired
  // twice for one click.
  const dismiss = useCallback(() => setDismissed(deployed), [deployed]);

  return { outdated, deployed, running, check, dismiss };
}
